import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  useMediaQuery,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { FaUserPlus } from 'react-icons/fa';

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL
import { API_URL } from '../utils/apiConfig';

const OnboardEmployee = ({ onClose, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // State management
  const [employeeInfo, setEmployeeInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    hireDate: "",
    phone: "",
    position: "",
    store: "",
    payRate: "",
    hireType: "new",
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  const [selectedForms, setSelectedForms] = useState({});
  const [availableForms, setAvailableForms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState({}); // Track form validation errors

  // Fetch locations on component mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}/locations`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) throw new Error("Failed to fetch locations");

        const data = await response.json();
        setLocations(data);
      } catch (err) {
        console.error("Error fetching locations:", err);
        setError("Failed to load store locations. Please try again.");
      }
    };

    fetchLocations();
  }, []);

  // Fetch available forms on component mount
  useEffect(() => {
    const fetchForms = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}/onboarding_letters`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch forms");

        const forms = await response.json();
        setAvailableForms(forms);

        // Initialize selectedForms with all forms checked
        const initialSelectedForms = forms.reduce((acc, form) => {
          acc[form.slug] = { selected: true, isCompleted: 0 };
          return acc;
        }, {});
        setSelectedForms(initialSelectedForms);
      } catch (err) {
        console.error("Error fetching forms:", err);
        setError("Failed to load onboarding forms. Please try again.");
      }
    };

    fetchForms();
  }, []);

  // Validate form fields
  const validateForm = () => {
    const errors = {};

    // Trim spaces from first and last names
    const trimmedFirstName = employeeInfo.firstName.trim();
    if (trimmedFirstName === "") {
      errors.firstName = "First name is required.";
    }

    const trimmedLastName = employeeInfo.lastName.trim();
    if (trimmedLastName === "") {
      errors.lastName = "Last name is required.";
    }

    if (!employeeInfo.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      errors.email = "Please enter a valid email address.";
    }

    if (employeeInfo.hireDate === "") {
      errors.hireDate = "Hire date is required.";
    }

    if (!employeeInfo.phone.match(/^\d{10}$/)) {
      errors.phone = "Please enter a valid 10-digit phone number.";
    }

    if (employeeInfo.position.trim() === "") {
      errors.position = "Position is required.";
    }

    if (employeeInfo.store === "") {
      errors.store = "Store is required.";
    }

    if (employeeInfo.payRate === "" || isNaN(employeeInfo.payRate)) {
      errors.payRate = "Please enter a valid pay rate.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0; // Return true if no errors
  };

  const handleDialogConfirm = async () => {
    // Validate form before proceeding
    if (!validateForm()) {
      return;
    }

    setProcessing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const token = localStorage.getItem("token");

      // Trim trailing spaces from first and last names before sending
      const trimmedFirstName = employeeInfo.firstName.trim();
      const trimmedLastName = employeeInfo.lastName.trim();
      const employeePayload = {
        ...employeeInfo,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        name: `${trimmedFirstName} ${trimmedLastName}`,
        locationId: employeeInfo.store, // Map store to locationId for backend
      };
      
      // Remove the 'store' field as backend expects 'locationId'
      delete employeePayload.store;

      console.log('Creating employee with payload:', employeePayload);

      // Filter and format the selected forms
      const formattedForms = availableForms
        .filter(form => {
          if (form.slug === "offerLetter") return true;
          return selectedForms[form.slug]?.selected === true;
        })
        .map(form => ({
          formId: form.id,
          isCompleted: 0
        }));

      // Create employee with forms
      const employeeResponse = await fetch(`${API_URL}/employees`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          employee: employeePayload,
          forms: formattedForms
        }),
      });

      if (!employeeResponse.ok) {
        const errorData = await employeeResponse.json();
        throw new Error(errorData.error || "Failed to create employee");
      }

      const { employeeId } = await employeeResponse.json();
      console.log('Employee created with ID:', employeeId);

      // Find the store name from the locations array
      const selectedLocation = locations.find(loc => loc.id === employeeInfo.store);
      const storeName = selectedLocation ? selectedLocation.name : '';

      // Generate offer letter with all necessary data for PDF generation
      const offerLetterPayload = {
        employeeId,
        employeeData: {
          ...employeePayload,
          formattedHireDate: new Date(employeeInfo.hireDate).toLocaleDateString(),
          storeName: storeName,
          position: employeeInfo.position,
          formattedPayRate: employeeInfo.payRate ? 
            `$${Number(employeeInfo.payRate).toFixed(2)}` : 
            '$0.00'
        }
      };

      console.log('Generating offer letter with payload:', offerLetterPayload);

      const offerLetterResponse = await fetch(`${API_URL}/employees/offer-letter`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(offerLetterPayload),
      });

      if (!offerLetterResponse.ok) {
        const errorData = await offerLetterResponse.json();
        throw new Error(errorData.error || "Failed to generate offer letter");
      }

      const { filePath } = await offerLetterResponse.json();
      console.log('Offer letter generated:', filePath);

      // Show success animation and close dialog
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setDialogOpen(false);
        resetForm();
      }, 2000);

      setSuccessMessage(`Successfully onboarded ${trimmedFirstName} ${employeeInfo.lastName}`);

    } catch (err) {
      console.error("Error during onboarding:", err);
      setError(err.message || "An error occurred during onboarding");
    } finally {
      setProcessing(false);
    }
  };

  // Reset form state
  const resetForm = () => {
    setEmployeeInfo({
      firstName: "",
      lastName: "",
      email: "",
      hireDate: "",
      phone: "",
      position: "",
      store: "",
      payRate: "",
      hireType: "new",
    });

    // Reset form selections to initial state
    const initialSelectedForms = availableForms.reduce((acc, form) => {
      acc[form.slug] = { selected: true, isCompleted: 0 };
      return acc;
    }, {});
    setSelectedForms(initialSelectedForms);

    // Clear form errors
    setFormErrors({});
  };

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#ffffff'
    }}>
      {/* Header with close button */}
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6">
          Onboard New Employee
        </Typography>
        {onClose && (
          <IconButton 
            onClick={onClose}
            sx={{ 
              color: 'text.secondary'
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Scrollable content */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto',
        p: 2
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="First Name"
              name="firstName"
              value={employeeInfo.firstName}
              onChange={(e) =>
                setEmployeeInfo((prev) => ({ ...prev, firstName: e.target.value }))
              }
              fullWidth
              required
              error={!!formErrors.firstName}
              helperText={formErrors.firstName}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Last Name"
              name="lastName"
              value={employeeInfo.lastName}
              onChange={(e) =>
                setEmployeeInfo((prev) => ({ ...prev, lastName: e.target.value }))
              }
              fullWidth
              required
              error={!!formErrors.lastName}
              helperText={formErrors.lastName}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Email Address"
              name="email"
              type="email"
              value={employeeInfo.email}
              onChange={(e) =>
                setEmployeeInfo((prev) => ({ ...prev, email: e.target.value }))
              }
              fullWidth
              required
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Hire Type</FormLabel>
              <RadioGroup
                row
                name="hireType"
                value={employeeInfo.hireType}
                onChange={(e) => setEmployeeInfo((prev) => ({ ...prev, hireType: e.target.value }))}
              >
                <FormControlLabel value="new" control={<Radio />} label="New Hire" />
                <FormControlLabel value="rehire" control={<Radio />} label="Rehire" />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Hire Date"
              name="hireDate"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={employeeInfo.hireDate}
              onChange={(e) =>
                setEmployeeInfo((prev) => ({ ...prev, hireDate: e.target.value }))
              }
              fullWidth
              required
              error={!!formErrors.hireDate}
              helperText={formErrors.hireDate}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Phone Number (no spaces)"
              name="phone"
              type="tel"
              value={employeeInfo.phone}
              onChange={(e) =>
                setEmployeeInfo((prev) => ({ ...prev, phone: e.target.value }))
              }
              fullWidth
              required
              error={!!formErrors.phone}
              helperText={formErrors.phone}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Position"
              name="position"
              value={employeeInfo.position}
              onChange={(e) =>
                setEmployeeInfo((prev) => ({ ...prev, position: e.target.value }))
              }
              fullWidth
              required
              error={!!formErrors.position}
              helperText={formErrors.position}
            />
          </Grid>

          <Grid item xs={12}>
            <Select
              value={employeeInfo.store}
              onChange={(e) =>
                setEmployeeInfo((prev) => ({ ...prev, store: e.target.value }))
              }
              displayEmpty
              fullWidth
              required
              error={!!formErrors.store}
            >
              <MenuItem value="" disabled>
                Select Store
              </MenuItem>
              {locations.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.name}
                </MenuItem>
              ))}
            </Select>
            {formErrors.store && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {formErrors.store}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Pay Rate"
              name="payRate"
              type="number"
              value={employeeInfo.payRate}
              onChange={(e) =>
                setEmployeeInfo((prev) => ({ ...prev, payRate: e.target.value }))
              }
              fullWidth
              required
              error={!!formErrors.payRate}
              helperText={formErrors.payRate}
            />
          </Grid>

          {/* Onboarding Forms */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Select Forms to Include
            </Typography>
            <Grid container spacing={1}>
              {availableForms.map((form) => (
                <Grid item xs={12} sm={6} key={form.id}>
                  <Box display="flex" alignItems="center">
                    <input
                      type="checkbox"
                      id={`form-${form.id}`}
                      checked={
                        form.slug === "offerLetter"
                          ? true
                          : selectedForms[form.slug] ?? true
                      }
                      onChange={(e) => {
                        if (form.slug !== "offerLetter") {
                          setSelectedForms((prev) => ({
                            ...prev,
                            [form.slug]: e.target.checked,
                          }));
                        }
                      }}
                      disabled={form.slug === "offerLetter"}
                    />
                    <label htmlFor={`form-${form.id}`} style={{ marginLeft: 8 }}>
                      {form.name}
                    </label>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Action button at bottom */}
      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid #e0e0e0',
        backgroundColor: '#ffffff'
      }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => setDialogOpen(true)} 
          fullWidth
          sx={{ }}
        >
          Onboard Employee
        </Button>
      </Box>

      <Dialog 
        open={dialogOpen} 
        onClose={() => !processing && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ position: 'relative', minHeight: '200px' }}>
          <AnimatePresence>
            {processing && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  zIndex: 10,
                }}
              >
                <CircularProgress size={60} />
                <Typography sx={{ mt: 2 }}>
                  Creating employee profile and documents...
                </Typography>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {success && (
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  zIndex: 20,
                }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ 
                    type: "spring",
                    stiffness: 260,
                    damping: 20 
                  }}
                >
                  <CheckCircle2 
                    size={80} 
                    color="#4CAF50"
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    Employee Successfully Onboarded!
                  </Typography>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <Typography sx={{ fontSize: '1.5rem' }}>
            Are you sure you want to onboard {employeeInfo.firstName.trim()} {employeeInfo.lastName.trim()}?
          </Typography>
          <FaUserPlus size={100} color="#4CAF50" style={{ marginTop: '20px', margin: '50 auto', textAlign: 'center', width: '100%' }} /> 
        </DialogContent>

        {!processing && !success && (
          <DialogActions sx={{ justifyContent: 'space-between', }}>
            <Button 
              onClick={() => setDialogOpen(false)} 
              disabled={processing}
              sx={{ fontSize: '1.2rem' }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDialogConfirm} 
              color="primary"
              disabled={processing}
              sx={{ fontSize: '1.2rem' }}
            >
              {processing ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default OnboardEmployee;